import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCVContext } from '@/context/CVContext';
import { useLanguage } from '@/context/LanguageContext';
import { useGamification } from '@/context/GamificationContext';
import { sessionApi, chatApi, profileApi } from '@/services/api';
import Header from '@/components/Header';
import ChatMessage from '@/components/ChatMessage';
import ProfileSidebar from '@/components/ProfileSidebar';
import VoiceInput from '@/components/VoiceInput';
import DocumentUpload from '@/components/DocumentUpload';
import CameraCapture from '@/components/CameraCapture';
import OnboardingTour from '@/components/OnboardingTour';
import { useOpenAITTS } from '@/hooks/useOpenAITTS';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Loader2, FileText, Volume2, VolumeX, Smile, Paperclip, Mic, Camera, Clock, Sparkles, AudioLines, FileEdit, Menu, X, ChevronRight, User, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getEncouragementMessage, getRandomTip } from '@/utils/celebrations';
import { calculateCompletion, getIncompleteSections } from '@/utils/completionCalculator';
import { trackCVFirstMessage, trackCV50Percent, trackCV90Percent, trackCVDownloadInitiated } from '@/lib/posthog';
import ScoreCelebration from '@/components/ScoreCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CVChat() {
  const navigate = useNavigate();
  const { state, dispatch } = useCVContext();
  const { profile, session, messages, interactionMode, entryType, isLoading } = state;
  const { t, language } = useLanguage();
  
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false); // Voice conversation mode
  const [voiceRecordingActive, setVoiceRecordingActive] = useState(false); // Track if voice input is recording/previewing
  const [hasFeedback, setHasFeedback] = useState(false); // Track if CV feedback exists
  const [lastCompletionPercentage, setLastCompletionPercentage] = useState(0); // Track CV progress
  const [showMobileSidebar, setShowMobileSidebar] = useState(false); // Mobile sidebar toggle
  const [showCelebration, setShowCelebration] = useState(false); // Score celebration modal
  const [celebrationData, setCelebrationData] = useState(null); // Celebration score data
  const [incompleteSections, setIncompleteSections] = useState([]); // Suggestion chips
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasTrackedFirstMessage = useRef(false);
  const hasTracked50 = useRef(false);
  const hasTracked90 = useRef(false);
  const prevProgressRef = useRef(0);
  
  // Gamification hook
  const { recordAction, addPoints, checkBadges, incrementStreak, points } = useGamification();
  
  // OpenAI TTS hook
  const { speak, stop, isSpeaking, isLoading: ttsLoading, isSupported: ttsSupported } = useOpenAITTS(language);

  // Check for CV feedback on mount and when messages change
  useEffect(() => {
    const feedback = localStorage.getItem('cvFeedback');
    setHasFeedback(!!feedback);
  }, [messages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Track progress milestones and update suggestion chips
  useEffect(() => {
    if (!profile) return;
    const { completion } = calculateCompletion(profile);
    const prevProgress = prevProgressRef.current;
    
    // Score jump toast (>10 point jump)
    if (prevProgress > 0 && completion - prevProgress > 10) {
      const delta = completion - prevProgress;
      toast.success(`🚀 +${delta}%! ${language === 'en' ? `Your CV jumped to ${completion}%!` : `Votre CV est passé à ${completion}% !`}`, {
        duration: 4000,
      });
    }
    
    // PostHog funnel events
    if (session?.id) {
      if (!hasTracked50.current && completion >= 50) {
        hasTracked50.current = true;
        trackCV50Percent(session.id);
      }
      if (!hasTracked90.current && completion >= 90) {
        hasTracked90.current = true;
        trackCV90Percent(session.id);
      }
    }
    
    prevProgressRef.current = completion;
    
    // Update incomplete section chips
    const incomplete = getIncompleteSections(profile, language);
    setIncompleteSections(incomplete.slice(0, 3));
  }, [profile, session?.id, language]);

  // Initialize session — runs ONCE on mount
  const sessionInitRef = useRef(false);
  
  useEffect(() => {
    // Prevent duplicate session creation from React re-renders
    if (sessionInitRef.current) return;
    
    const initSession = async () => {
      // Read all state from localStorage first (single source of truth)
      let currentProfile = profile;
      let currentEntryType = entryType;
      let currentInteractionMode = interactionMode;
      
      // URL param fallback for entry type
      const urlParams = new URLSearchParams(window.location.search);
      const urlEntry = urlParams.get('entry');
      if (urlEntry) {
        currentEntryType = urlEntry;
        dispatch({ type: 'SET_ENTRY_TYPE', payload: urlEntry });
      }
      
      // If not in state, try localStorage
      if (!currentProfile) {
        try {
          const storedProfileStr = localStorage.getItem('cvProfile');
          if (storedProfileStr) {
            currentProfile = JSON.parse(storedProfileStr);
            dispatch({ type: 'SET_PROFILE', payload: currentProfile });
          }
          
          const storedEntryType = localStorage.getItem('cvEntryType');
          if (storedEntryType && !currentEntryType) {
            currentEntryType = storedEntryType;
            dispatch({ type: 'SET_ENTRY_TYPE', payload: storedEntryType });
          }
          
          const storedInteractionMode = localStorage.getItem('cvInteractionMode');
          if (storedInteractionMode) {
            currentInteractionMode = storedInteractionMode;
            dispatch({ type: 'SET_INTERACTION_MODE', payload: storedInteractionMode });
          }
        } catch (e) {
          console.error('CVChat: Error loading from localStorage', e);
        }
      }
      
      console.log('CVChat: initSession', { 
        profile: !!currentProfile, 
        entryType: currentEntryType, 
        interactionMode: currentInteractionMode 
      });
      
      if (!currentProfile) {
        console.log('CVChat: No profile, redirecting to home');
        navigate('/');
        return;
      }

      // Mark as initialized BEFORE the async call to prevent re-entry
      sessionInitRef.current = true;
      
      // Check if we already have a valid session in context or localStorage
      let existingSession = session;
      if (!existingSession) {
        try {
          const storedSession = localStorage.getItem('cvSession');
          if (storedSession) {
            existingSession = JSON.parse(storedSession);
          }
        } catch (e) { /* ignore */ }
      }
      
      const hasValidSession = existingSession && 
        existingSession.messages && 
        existingSession.messages.length > 0 && 
        existingSession.profile_id === currentProfile.id;
      
      if (hasValidSession) {
        // Use existing session
        console.log('CVChat: Using existing session', existingSession.id);
        dispatch({ type: 'SET_SESSION', payload: existingSession });
        const existingMessages = existingSession.messages.map((msg, index) => ({
          id: msg.id || `existing-${index}-${Date.now()}`,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp || new Date().toISOString()
        }));
        dispatch({ type: 'SET_MESSAGES', payload: existingMessages });
        return;
      }
      
      // Create new session
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        console.log('CVChat: Creating new session with entry_type:', currentEntryType || 'create');
        const response = await sessionApi.create({
          profile_id: currentProfile.id,
          entry_type: currentEntryType || 'create',
          interaction_mode: currentInteractionMode || 'chat',
          language: language,
        });
        
        console.log('CVChat: Session created', { id: response.data.id, entry_type: response.data.entry_type, messagesCount: response.data.messages?.length });
        dispatch({ type: 'SET_SESSION', payload: response.data });
        
        // Load initial messages from session
        if (response.data.messages && response.data.messages.length > 0) {
          const initialMessages = response.data.messages.map((msg, index) => ({
            id: `init-${index}-${Date.now()}`,
            role: msg.role,
            content: msg.content,
            timestamp: new Date().toISOString()
          }));
          dispatch({ type: 'SET_MESSAGES', payload: initialMessages });
          
          // If this is an "improve" session, save feedback + trigger celebration
          if (response.data.entry_type === 'improve') {
            const fullFeedback = response.data.cv_feedback_full || initialMessages[0]?.content;
            if (fullFeedback) {
              const feedbackData = {
                content: fullFeedback,
                timestamp: new Date().toISOString(),
                profileId: currentProfile.id,
                scoreBefore: response.data.score_before ?? null,
                scoreAfter: response.data.score_after ?? null,
                tipsCount: response.data.tips_count ?? 0,
              };
              localStorage.setItem('cvFeedback', JSON.stringify(feedbackData));
              
              // Trigger celebration modal if scores are present
              if (feedbackData.scoreBefore != null && feedbackData.scoreAfter != null) {
                setCelebrationData(feedbackData);
                setShowCelebration(true);
              }
            }
          }
          
          if (autoSpeak && initialMessages[0]) {
            speak(initialMessages[0].content);
          }
        }
      } catch (error) {
        console.error('Error creating session:', error);
        sessionInitRef.current = false; // Allow retry on error
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshProfile = async () => {
    if (profile?.id) {
      try {
        const response = await profileApi.get(profile.id);
        dispatch({ type: 'SET_PROFILE', payload: response.data });
        return response.data;
      } catch (error) {
        console.error('Error refreshing profile:', error);
        return null;
      }
    }
    return null;
  };

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || !session) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString(),
    };

    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
    setInput('');
    setSuggestions([]);
    setIsSending(true);
    setIsTyping(true);

    // Track first message
    if (!hasTrackedFirstMessage.current && session?.id) {
      hasTrackedFirstMessage.current = true;
      trackCVFirstMessage(session.id);
    }

    try {
      const response = await chatApi.send(session.id, messageText.trim(), language);
      const { message, current_section, profile_updates, suggestions: newSuggestions, is_complete } = response.data;

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: message,
        timestamp: new Date().toISOString(),
      };
      
      dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
      dispatch({ type: 'SET_CURRENT_SECTION', payload: current_section });
      
      if (newSuggestions?.length > 0) {
        setSuggestions(newSuggestions);
      }

      // Speak the response if auto-speak is enabled or voice mode is on
      if (autoSpeak || voiceMode) {
        speak(message, { language });
      }

      // Always refresh profile after chat response to update sidebar
      const updatedProfile = await refreshProfile();
      
      // Record action for gamification
      recordAction('chat_message');
      
      // Check if CV improved - show encouragement
      if (updatedProfile && updatedProfile.completion_percentage) {
        const newCompletion = updatedProfile.completion_percentage;
        if (newCompletion > lastCompletionPercentage) {
          // CV improved! Show encouragement
          const encouragement = getEncouragementMessage(newCompletion, language);
          toast.success(encouragement, {
            duration: 3000,
            icon: newCompletion >= 75 ? '🎉' : newCompletion >= 50 ? '💪' : '🚀'
          });
          setLastCompletionPercentage(newCompletion);
          
          // Award extra points for improving CV
          addPoints(5, language === 'en' ? 'CV Improved' : 'CV Amélioré');
        }
      }
      
      // Show random tip occasionally (20% chance)
      if (Math.random() < 0.2) {
        setTimeout(() => {
          const tip = getRandomTip(language);
          toast.info(tip, { duration: 5000 });
        }, 2000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = language === 'en' 
        ? "Sorry, I encountered an error. Can you try again?"
        : "Désolé, j'ai rencontré une erreur. Pouvez-vous réessayer ?";
      dispatch({ type: 'ADD_MESSAGE', payload: {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: errorMsg,
        timestamp: new Date().toISOString(),
      }});
    } finally {
      setIsSending(false);
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleSidebarSectionClick = (sectionKey, sectionLabel) => {
    const message = language === 'en'
      ? `I want to improve my ${sectionLabel.toLowerCase()} section`
      : `Je veux améliorer ma section ${sectionLabel.toLowerCase()}`;
    handleSend(message);
  };

  const handleDocumentUpload = async (file, documentType) => {
    if (!profile || !session) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('profile_id', profile.id);
    formData.append('session_id', session.id);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/document/analyze`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        await refreshProfile();
        const sessionResponse = await sessionApi.get(session.id);
        dispatch({ type: 'SET_SESSION', payload: sessionResponse.data });
        
        if (autoSpeak && response.data.message) {
          speak(response.data.message);
        }
      } else {
        dispatch({ type: 'ADD_MESSAGE', payload: {
          id: Date.now().toString(),
          role: 'assistant',
          content: "Je n'ai pas pu extraire les informations de ce document. Essayez avec une image plus nette ou saisissez les informations manuellement.",
          timestamp: new Date().toISOString(),
        }});
      }
    } catch (error) {
      console.error('Document upload error:', error);
      dispatch({ type: 'ADD_MESSAGE', payload: {
        id: Date.now().toString(),
        role: 'assistant',
        content: "Erreur lors de l'analyse du document. Veuillez réessayer.",
        timestamp: new Date().toISOString(),
      }});
    }
  };

  const handleSpeakMessage = (content) => {
    if (isSpeaking) {
      stop();
    } else {
      speak(content, { language });
    }
  };

  const toggleAutoSpeak = () => {
    if (autoSpeak) {
      stop();
    }
    setAutoSpeak(!autoSpeak);
  };

  const toggleVoiceMode = () => {
    if (voiceMode) {
      stop();
    }
    setVoiceMode(!voiceMode);
  };

  if (isLoading || !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#081B19] via-[#0d2926] to-[#081B19] flex items-center justify-center p-4">
        <div className="text-center max-w-sm w-full">
          {/* Animated EdoMatch logo */}
          <div className="relative mb-8">
            <div className="w-24 h-24 mx-auto rounded-2xl overflow-hidden shadow-2xl shadow-[#5BA946]/30">
              <img 
                src="/edomatch-icon.png" 
                alt="EdoMatch" 
                className="w-full h-full object-cover animate-pulse"
              />
            </div>
            {/* Animated rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 rounded-full border-2 border-[#5BA946]/30 animate-ping" style={{ animationDuration: '2s' }} />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-40 rounded-full border border-[#5BA946]/20 animate-ping" style={{ animationDuration: '3s' }} />
            </div>
          </div>
          
          {/* Text content */}
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('preparingSession')}
          </h2>
          <p className="text-gray-400 mb-6">
            {t('coachPreparing')}
          </p>
          
          {/* Progress bar */}
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
            <div 
              className="h-full bg-gradient-to-r from-[#5BA946] to-[#6dc055] rounded-full animate-progress"
              style={{
                animation: 'progressAnimation 3s ease-in-out infinite'
              }}
            />
          </div>
          
          {/* Time estimate */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm text-gray-300">
            <Clock className="w-4 h-4 text-[#5BA946]" />
            <span>{t('estimatedTime')}</span>
          </div>
        </div>
        
        {/* CSS for progress animation */}
        <style>{`
          @keyframes progressAnimation {
            0% { width: 0%; }
            50% { width: 70%; }
            100% { width: 100%; }
          }
          .animate-progress {
            animation: progressAnimation 3s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
      <Header 
        showMobileMenuButton={true}
        onOpenMobileMenu={() => setShowMobileSidebar(true)}
      />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-2 sm:px-4 py-4 sm:py-6 flex gap-4 sm:gap-6 relative">
        {/* Mobile Sidebar Overlay */}
        {showMobileSidebar && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
            onClick={() => setShowMobileSidebar(false)}
          />
        )}
        
        {/* Mobile Sidebar Drawer - Optimized */}
        <div className={`
          fixed top-0 right-0 h-full w-full sm:w-[350px] sm:max-w-[85vw] bg-white z-50 transform transition-transform duration-300 ease-in-out lg:hidden
          ${showMobileSidebar ? 'translate-x-0' : 'translate-x-full'}
        `}>
          {/* Header - Fixed */}
          <div className="sticky top-0 bg-gradient-to-r from-[#5BA946] to-emerald-600 px-4 py-3 flex items-center justify-between z-10 safe-area-top">
            <h3 className="font-semibold text-white text-base">
              {language === 'en' ? 'My Progress' : 'Ma Progression'}
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMobileSidebar(false)}
              className="text-white hover:bg-white/20 h-9 w-9"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Scrollable Content */}
          <div className="h-[calc(100%-56px)] overflow-y-auto overscroll-contain pb-safe">
            <div className="p-3 sm:p-4">
              <ProfileSidebar onSectionClick={handleSidebarSectionClick} />
            </div>
          </div>
        </div>

        {/* Chat Area - WhatsApp Style */}
        <div className="flex-1 flex flex-col min-h-0" data-testid="chat-area">
          <div className="flex-1 flex flex-col bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            
            {/* Chat Header - WhatsApp Style - Hidden on mobile, visible on desktop */}
            <div className="hidden lg:flex bg-[#081B19] text-white px-3 sm:px-4 py-3 items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-[#5BA946] flex-shrink-0">
                  <img 
                    src="/edomatch-icon.png" 
                    alt="EdoMatch" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base truncate">{t('coachCVEdoMatch')}</h3>
                  <p className="text-xs text-gray-300">
                    {isSpeaking ? (language === 'en' ? 'speaking...' : 'parle...') : isTyping ? t('typing') : t('online')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                {/* View CV Button */}
                <Button 
                  onClick={() => navigate('/preview')}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 h-8 sm:h-9 px-2 sm:px-3"
                  data-testid="preview-btn"
                >
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline ml-1 text-xs">{language === 'en' ? 'View CV' : 'Voir CV'}</span>
                </Button>
                
                {/* Mobile Progress Toggle Button */}
                <Button
                  onClick={() => setShowMobileSidebar(true)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 h-8 sm:h-9 px-2 lg:hidden"
                  data-testid="mobile-progress-btn"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Messages Area - WhatsApp Style with chat pattern background */}
            <div 
              className="flex-1 overflow-y-auto px-3 sm:px-4 py-4"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e5ded8' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundColor: '#ECE5DD'
              }}
            >
              <div className="space-y-1 max-w-3xl mx-auto">
                {messages.map((msg, index) => (
                  <ChatMessage 
                    key={msg.id || index} 
                    message={msg}
                    onSpeak={ttsSupported ? () => handleSpeakMessage(msg.content) : null}
                  />
                ))}
                {isTyping && (
                  <ChatMessage 
                    message={{ role: 'assistant', content: '' }} 
                    isTyping 
                  />
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Quick Suggestions */}
            {suggestions.length > 0 && (
              <div className="px-3 sm:px-4 py-2 bg-white border-t border-gray-100 flex flex-wrap gap-2">
                {suggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="text-xs bg-white hover:bg-[#5BA946]/10 hover:border-[#5BA946] hover:text-[#5BA946] rounded-full"
                    onClick={() => handleSuggestionClick(suggestion)}
                    data-testid={`suggestion-${index}`}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            )}

            {/* Suggestion Chips for Incomplete Sections */}
            {incompleteSections.length > 0 && !isSending && messages.length >= 2 && (
              <div className="px-3 sm:px-4 py-2 bg-[#F0F2F5] border-t border-gray-100 flex flex-wrap gap-2">
                <span className="text-xs text-gray-400 w-full mb-1">
                  {language === 'en' ? '💡 Quick actions:' : '💡 Actions rapides :'}
                </span>
                {incompleteSections.map((section) => (
                  <button
                    key={section.key}
                    onClick={() => handleSend(section.message)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-xs font-medium text-gray-700 hover:bg-[#5BA946]/10 hover:text-[#5BA946] border border-gray-200 hover:border-[#5BA946] transition-all shadow-sm"
                  >
                    <span>{section.emoji}</span>
                    <span>{section.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Input Area - WhatsApp Style */}
            <div className="bg-[#F0F2F5] px-2 sm:px-4 py-2 sm:py-3 border-t border-gray-200">
              <div className="max-w-3xl mx-auto">
                {/* Voice Input - Single component that handles all states */}
                <VoiceInput 
                  onTranscript={(text) => {
                    if (text.trim()) {
                      handleSend(text.trim());
                    }
                  }}
                  onStateChange={setVoiceRecordingActive}
                  disabled={isSending}
                  language={language === 'en' ? 'en-US' : 'fr-FR'}
                  showInline={!voiceRecordingActive}
                  inputComponent={
                    <div className="flex items-center gap-1 sm:gap-2 flex-1">
                      {/* Attachment buttons */}
                      <div className="flex items-center">
                        <DocumentUpload 
                          onUpload={handleDocumentUpload}
                          disabled={isSending}
                          className="text-gray-500 hover:text-[#5BA946]"
                        />
                        <CameraCapture
                          onCapture={handleDocumentUpload}
                          disabled={isSending}
                          className="text-gray-500 hover:text-[#5BA946]"
                        />
                      </div>
                      
                      {/* Input field */}
                      <div className="flex-1 relative">
                        <textarea
                          ref={inputRef}
                          value={input}
                          onChange={(e) => {
                            setInput(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSend();
                            }
                          }}
                          placeholder={t('typeMessage')}
                          disabled={isSending}
                          rows={1}
                          className="w-full rounded-2xl bg-white border-0 shadow-sm px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#5BA946] resize-none overflow-hidden"
                          style={{ minHeight: '44px', maxHeight: '120px' }}
                          data-testid="chat-input"
                        />
                      </div>
                      
                      {/* Send button when there's text */}
                      {input.trim() && (
                        <Button 
                          onClick={() => handleSend()}
                          disabled={isSending}
                          className="rounded-full w-10 h-10 sm:w-12 sm:h-12 p-0 bg-[#5BA946] hover:bg-[#4a9339] shadow-md"
                          data-testid="send-btn"
                        >
                          {isSending ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Send className="w-5 h-5" />
                          )}
                        </Button>
                      )}
                    </div>
                  }
                />
              </div>
            </div>
          </div>

          {/* Conversation Summary Banner - Shows after some interaction */}
          {messages.length >= 4 && (
            <div className="mt-4 bg-gradient-to-r from-[#081B19] to-[#0d3d36] rounded-xl p-4 sm:p-5 shadow-lg" data-testid="conversation-summary-banner">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#5BA946] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-base sm:text-lg">
                    {language === 'en' ? "Great progress! What's next?" : "Super avancement ! Et maintenant ?"}
                  </h3>
                  <p className="text-gray-300 text-sm mt-1">
                    {(() => {
                      const { completion } = calculateCompletion(profile);
                      return language === 'en' 
                        ? `Your CV is ${completion}% complete. Here are your next steps:`
                        : `Votre CV est complété à ${completion}%. Voici vos prochaines étapes :`;
                    })()}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                {/* Download CV CTA */}
                <Button 
                  onClick={() => {
                    if (session?.id) trackCVDownloadInitiated(session.id, 'banner');
                    navigate('/preview');
                  }}
                  className="bg-[#5BA946] hover:bg-[#4a9339] text-white gap-2 h-auto py-3"
                  data-testid="banner-download-cv-btn"
                >
                  <FileText className="w-4 h-4" />
                  <span className="text-sm">{language === 'en' ? 'Download CV' : 'Télécharger CV'}</span>
                </Button>
                
                {/* View Complete Profile CTA */}
                <Button 
                  onClick={() => navigate('/complete-profile')}
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 h-auto py-3"
                  data-testid="banner-complete-profile-btn"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm">{language === 'en' ? 'Complete Profile' : 'Profil Complet'}</span>
                </Button>
                
                {/* Personality Test CTA - Only if not done */}
                {!localStorage.getItem('personalityResult') ? (
                  <Button 
                    onClick={() => navigate('/personality')}
                    variant="outline"
                    className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-400/50 text-white hover:from-purple-500/30 hover:to-pink-500/30 gap-2 h-auto py-3"
                    data-testid="banner-personality-btn"
                  >
                    <Sparkles className="w-4 h-4 text-purple-300" />
                    <span className="text-sm">{language === 'en' ? 'Personality Test' : 'Test Personnalité'}</span>
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate('/personality-result')}
                    variant="outline"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 h-auto py-3"
                    data-testid="banner-view-personality-btn"
                  >
                    <Star className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm">{language === 'en' ? 'My Soft Power' : 'Mon Soft Power'}</span>
                  </Button>
                )}
              </div>
              
              {/* CV Feedback link if available */}
              {localStorage.getItem('cvFeedback') && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <Button 
                    onClick={() => navigate('/cv-feedback')}
                    variant="ghost"
                    className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 gap-2 w-full justify-center"
                    data-testid="banner-feedback-btn"
                  >
                    <FileEdit className="w-4 h-4" />
                    <span className="text-sm">{language === 'en' ? 'View CV Feedback Report' : 'Voir le Rapport de Feedback CV'}</span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Quick Action Buttons below chat - Shows when banner is not visible */}
          {messages.length < 4 && (
            <div className="mt-3 flex flex-wrap gap-2 justify-center">
              <Button 
                onClick={() => navigate('/preview')}
                variant="outline"
                className="gap-2 border-[#5BA946] text-[#5BA946] hover:bg-[#5BA946] hover:text-white"
                data-testid="view-cv-btn"
              >
                <FileText className="w-4 h-4" />
                {language === 'en' ? 'View my CV' : 'Voir mon CV'}
              </Button>
              
              {/* CV Feedback button - check localStorage directly for reliability */}
              {(() => {
                const feedbackExists = localStorage.getItem('cvFeedback');
                return feedbackExists ? (
                  <Button 
                    onClick={() => navigate('/cv-feedback')}
                    variant="outline"
                    className="gap-2 border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white"
                    data-testid="view-feedback-btn"
                  >
                    <FileEdit className="w-4 h-4" />
                    {language === 'en' ? 'View Feedback Report' : 'Voir le Rapport de Feedback'}
                  </Button>
                ) : null;
              })()}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="hidden lg:block w-80" data-testid="profile-sidebar">
          <ProfileSidebar onSectionClick={handleSidebarSectionClick} />
        </div>
      </main>
      
      {/* Mobile Floating View CV Button */}
      <div className="fixed bottom-20 right-4 lg:hidden z-30">
        <Button
          onClick={() => navigate('/preview')}
          className="rounded-full w-12 h-12 bg-[#5BA946] hover:bg-[#4a9339] shadow-lg flex items-center justify-center"
          data-testid="mobile-view-cv-btn"
        >
          <FileText className="w-5 h-5 text-white" />
        </Button>
      </div>
      
      {/* Onboarding Tour */}
      <OnboardingTour 
        entryType={entryType} 
        onOpenMobileMenu={() => setShowMobileSidebar(true)}
      />
      
      {/* Score Celebration Modal */}
      {showCelebration && celebrationData && (
        <ScoreCelebration
          firstName={profile?.first_name || ''}
          scoreBefore={celebrationData.scoreBefore}
          scoreAfter={celebrationData.scoreAfter}
          tipsCount={celebrationData.tipsCount}
          onSeeTips={() => setShowCelebration(false)}
          onClose={() => setShowCelebration(false)}
        />
      )}
    </div>
  );
}
